/**
 * Authoritative LA County incorporated-city roster for Analysis coverage figures.
 * Source: California CAP Database vNov2025 (County contains Los Angeles, Type=City) — 88 cities.
 * Do not infer membership from place GEOID prefixes.
 */

/** Cap DB vNov2025 LA County cities (exact population.location_name spelling where known). */
export const LA88_INCORPORATED_CITY_NAMES = [
  'Agoura Hills',
  'Alhambra',
  'Arcadia',
  'Artesia',
  'Avalon',
  'Azusa',
  'Baldwin Park',
  'Bell',
  'Bell Gardens',
  'Bellflower',
  'Beverly Hills',
  'Bradbury',
  'Burbank',
  'Calabasas',
  'Carson',
  'Cerritos',
  'Claremont',
  'Commerce',
  'Compton',
  'Covina',
  'Cudahy',
  'Culver City',
  'Diamond Bar',
  'Downey',
  'Duarte',
  'El Monte',
  'El Segundo',
  'Gardena',
  'Glendale',
  'Glendora',
  'Hawaiian Gardens',
  'Hawthorne',
  'Hermosa Beach',
  'Hidden Hills',
  'Huntington Park',
  'Industry',
  'Inglewood',
  'Irwindale',
  'La Canada Flintridge',
  'La Habra Heights',
  'La Mirada',
  'La Puente',
  'La Verne',
  'Lakewood',
  'Lancaster',
  'Lawndale',
  'Lomita',
  'Long Beach',
  'Los Angeles',
  'Lynwood',
  'Malibu',
  'Manhattan Beach',
  'Maywood',
  'Monrovia',
  'Montebello',
  'Monterey Park',
  'Norwalk',
  'Palmdale',
  'Palos Verdes Estates',
  'Paramount',
  'Pasadena',
  'Pico Rivera',
  'Pomona',
  'Rancho Palos Verdes',
  'Redondo Beach',
  'Rolling Hills',
  'Rolling Hills Estates',
  'Rosemead',
  'San Dimas',
  'San Fernando',
  'San Gabriel',
  'San Marino',
  'Santa Clarita',
  'Santa Fe Springs',
  'Santa Monica',
  'Sierra Madre',
  'Signal Hill',
  'South El Monte',
  'South Gate',
  'South Pasadena',
  'Temple City',
  'Torrance',
  'Vernon',
  'Walnut',
  'West Covina',
  'West Hollywood',
  'Westlake Village',
  'Whittier',
] as const;

const LA88_CITY_SET = new Set<string>(
  LA88_INCORPORATED_CITY_NAMES.map((n) => n.toLowerCase())
);

/** Census county FIPS for Los Angeles County (CA 06 + 037), numeric or zero-padded. */
export const LA_COUNTY_GEOFIPS_CANONICAL = 6037;

export function isLosAngelesCountyGeofipsId(geofips: string | number | null | undefined): boolean {
  if (geofips === null || geofips === undefined) return false;
  const raw = String(geofips).replace(/\.0+$/, '').trim();
  const stripped = raw.replace(/^0+/, '') || '0';
  return stripped === '6037' || stripped === '60037';
}

/** Normalize county labels from population.county / location names. */
export function normalizeLaCountyLabel(value: string | null | undefined): string {
  return (value || '')
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/,\s*CA$/i, '')
    .toLowerCase();
}

export function isLosAngelesCountyName(value: string | null | undefined): boolean {
  const n = normalizeLaCountyLabel(value);
  if (!n) return false;
  // Do not treat "Los Angeles" (the city) as the county.
  return n === 'los angeles county' || n === 'county of los angeles';
}

export function isLa88IncorporatedCityName(cityName: string | null | undefined): boolean {
  if (!cityName?.trim()) return false;
  return LA88_CITY_SET.has(cityName.trim().toLowerCase());
}

/**
 * Show the countywide coverage figure when the selected location is LA County
 * or any of the 88 Cap DB incorporated cities.
 *
 * @param locationName — UI location label
 * @param countyFromMetadata — population.county via getCountyForCity (cities only)
 * @param geographyType — optional City | County | State
 * @param geofips — optional resolved geofips (handles 6037 / 06037)
 */
export function shouldShowLaCountyInventoryCoverage(opts: {
  locationName: string;
  countyFromMetadata?: string | null;
  geographyType?: 'city' | 'county' | 'state' | null;
  geofips?: string | number | null;
}): boolean {
  const name = (opts.locationName || '').trim();
  if (!name) return false;

  if (opts.geofips != null && isLosAngelesCountyGeofipsId(opts.geofips)) {
    // County row only — city place codes must not use this branch alone
    if (opts.geographyType === 'county' || isLosAngelesCountyName(name)) {
      return true;
    }
  }

  if (opts.geographyType === 'county' && isLosAngelesCountyName(name)) {
    return true;
  }

  // Explicit county location label (even before geographyType resolves)
  if (/^los angeles county$/i.test(name)) {
    return true;
  }

  if (isLa88IncorporatedCityName(name)) {
    return true;
  }

  // County relationship from location service (not GEOID prefix)
  const county = opts.countyFromMetadata;
  if (county && normalizeLaCountyLabel(county) === 'los angeles county') {
    return true;
  }

  return false;
}

export const LA88_COVERAGE_FIGURE_ASSETS = {
  published: {
    src: `${process.env.PUBLIC_URL || ''}/figures/la88-published-inventory-years.png`,
    downloadName: 'LA88_published_inventory_years.png',
    alt:
      'Heatmap of documented published GHG inventory years across all 88 incorporated Los Angeles County cities. Sources include Michael Boswell, California CAP Database November 2025.',
  },
  climatePlans: {
    src: `${process.env.PUBLIC_URL || ''}/figures/la88-inventory-coverage-confirmed.png`,
    downloadName: 'LA88_inventory_coverage_confirmed.png',
    alt:
      'Heatmap comparing published GHG inventory years with ClimatePlans stored inventory coverage across all 88 incorporated Los Angeles County cities, including confirmed Glendale coverage for 2005 to 2023. Attribution to Michael Boswell, California CAP Database November 2025.',
  },
} as const;

/** Native figure pixel size (both PNGs identical). */
export const LA88_COVERAGE_FIGURE_WIDTH = 4400;
export const LA88_COVERAGE_FIGURE_HEIGHT = 3080;
